/**
 * Finance360 Ops dentro de Finance 360 (Módulo Avanzado «ops»). Dos pestañas:
 *  - Panel: el consumo de los clientes conectados y las alertas de mejora (sobre todo Azure).
 *  - Revisión de borradores: cada borrador recurrente de consumo como un libro (el borrador a la
 *    izquierda, el consumo del mes anterior y del mes a facturar a la derecha), actualizar, vista
 *    previa y aprobar. Ops solo cambia cantidades/montos y agrega productos nuevos; la FE se genera
 *    con el proceso normal de Finance 360.
 */
import { useEffect, useState } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { Settings, X } from 'lucide-react';
import { opsApi } from '../../../utils/api';
import useEscClose from '../../../hooks/useEscClose';
import PanelOps from './PanelOps';
import RevisionOps from './RevisionOps';

const TABS = [['panel', 'Panel'], ['revision', 'Revisión de borradores']];

export default function Finance360Ops() {
  const [tab, setTab] = useState(() => { try { return localStorage.getItem('ops.tab') || 'panel'; } catch { return 'panel'; } });
  const elegir = (t) => { setTab(t); try { localStorage.setItem('ops.tab', t); } catch { /* sin almacenamiento */ } };
  return (
    <div className="space-y-4">
      <nav className="flex gap-1 border-b border-gray-200 items-end">
        {TABS.map(([k, n]) => (
          <button key={k} onClick={() => elegir(k)}
            className={clsx('px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px', tab === k ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500 hover:text-gray-800')}>{n}</button>
        ))}
        <LecturaAuto />
      </nav>
      {tab === 'panel' ? <PanelOps irARevision={() => elegir('revision')} /> : <RevisionOps />}
    </div>
  );
}

/** Lectura automática: X minutos después de generarse los borradores, y de nuevo cada N horas. */
function LecturaAuto() {
  const [c, setC] = useState(null);
  const [abierto, setAbierto] = useState(false);
  useEffect(() => { opsApi.config().then(r => setC(r.data.data)).catch(() => {}); }, []);
  if (!c) return null;
  return (<>
    <button onClick={() => setAbierto(true)} className="ml-auto mb-1.5 inline-flex items-center gap-1.5 text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 text-gray-600 hover:border-indigo-400">
      <Settings className="h-3.5 w-3.5" />
      {c.lecturaAuto ? `Lectura automática: ${c.minutosDespues} min después de generar (${c.horaGeneracion})${Number(c.releerHoras) ? ` · cada ${c.releerHoras} h` : ''}` : 'Lectura automática apagada'}
    </button>
    {abierto && <ConfigLectura c={c} onClose={() => setAbierto(false)} onGuardar={(n) => { setC({ ...c, ...n }); setAbierto(false); }} />}
  </>);
}

function ConfigLectura({ c, onClose, onGuardar }) {
  useEscClose(onClose);
  const [f, setF] = useState({ lecturaAuto: c.lecturaAuto, minutosDespues: c.minutosDespues, releerHoras: c.releerHoras });
  const [guardando, setGuardando] = useState(false);
  const guardar = async () => {
    setGuardando(true);
    try { onGuardar((await opsApi.guardarConfig(f)).data.data); toast.success('Lectura automática guardada'); }
    catch (e) { toast.error(e.response?.data?.error || 'No se pudo guardar'); }
    finally { setGuardando(false); }
  };
  const campo = 'w-24 text-right tabular-nums border border-gray-200 rounded-lg px-2 py-1.5';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white border border-gray-200 rounded-2xl w-full max-w-xl">
        <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-gray-200">
          <div><h2 className="text-lg font-bold text-gray-900">Lectura automática</h2>
            <p className="text-xs text-gray-500">Ops lee a Microsoft, Azure, Sophos y Acronis solo, para que al revisar ya esté al día. Los recurrentes se generan a las {c.horaGeneracion}.</p></div>
          <button onClick={onClose} className="h-8 w-8 flex items-center justify-center border border-gray-200 rounded-lg text-gray-500" aria-label="Cerrar"><X className="h-4 w-4" /></button>
        </div>
        <div className="px-5 py-4 space-y-3 text-sm">
          <label className="flex items-center gap-2 font-semibold"><input type="checkbox" checked={f.lecturaAuto} onChange={(e) => setF({ ...f, lecturaAuto: e.target.checked })} /> Leer automáticamente</label>
          <div className="flex items-center justify-between gap-3"><span>Minutos después de generarse los borradores</span><input type="number" min="0" max="1440" value={f.minutosDespues} disabled={!f.lecturaAuto} onChange={(e) => setF({ ...f, minutosDespues: e.target.value })} className={campo} /></div>
          <div className="flex items-center justify-between gap-3"><span>Volver a leer cada (horas, 0 = no)</span><input type="number" min="0" max="168" value={f.releerHoras} disabled={!f.lecturaAuto} onChange={(e) => setF({ ...f, releerHoras: e.target.value })} className={campo} /></div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200">
          <button onClick={onClose} className="px-3.5 py-2 text-sm font-semibold border border-gray-300 rounded-lg text-gray-700">Cancelar</button>
          <button onClick={guardar} disabled={guardando} className="px-3.5 py-2 text-sm font-semibold border-[1.5px] border-indigo-600 text-indigo-700 rounded-lg disabled:opacity-40">Guardar</button>
        </div>
      </div>
    </div>
  );
}
